/**
 * Главная: где ученик в программе и что делать дальше.
 *
 * Сверху — четыре числа, которые честно отражают рост (уроки, фразы в
 * кармане, освоенные навыки, серия), затем ступень со следующим уроком.
 * Сервисы живут на своей вкладке нижнего бара. Программа и карман приходят
 * с сервера; серия — локальная, это ритм занятий на этом устройстве.
 */

import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import { NextStepCard } from '../components/learn/NextStepCard';
import { CourseComingSoon } from '../components/CourseComingSoon';
import { Card } from '../components/Card';
import { ScrollReveal } from '../components/bits';
import { IconBrain, IconCards, IconFlame, IconSparkles, type AppIcon } from '../components/icons';
import { Quokka } from '../components/Quokka';
import { LEVEL_COUNT, levelCanDo, levelName, levelStep } from '../content/levels';
import { usePointerParallax } from '../hooks/usePointerParallax';
import { useLearningOverview, type LearningOverview } from '../hooks/useLearningOverview';
import { useLessonPrefetch } from '../hooks/useLessonPrefetch';
import { useProfile } from '../hooks/useProfile';
import { SandboxCard } from '../components/guide/SandboxCard';
import { PlansCard } from '../components/home/PlansCard';
import { ReviewDueCard } from '../components/home/ReviewDueCard';
import { SaveProgressCard } from '../components/home/SaveProgressCard';
import { useSandbox } from '../store/guideStore';
import { useUserStore } from '../store/userStore';
import { useTranslation } from 'react-i18next';

function Stat({ icon: StatIcon, value, label, tone }: { readonly icon: AppIcon; readonly value: string; readonly label: string; readonly tone: string }) {
  return (
    <Card animate={false} className="flex h-full min-w-0 flex-col gap-2">
      <span className={`grid h-10 w-10 place-items-center rounded-2xl ${tone}`}>
        <StatIcon size={20} aria-hidden="true" />
      </span>
      <p className="font-heading text-3xl font-black tabular-nums text-heading sm:text-4xl">{value}</p>
      <p className="text-sm leading-snug text-muted">{label}</p>
    </Card>
  );
}

function Hero({ overview }: { readonly overview: LearningOverview }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const level = overview.map.currentLevel;
  const skills = overview.map.levels.find((entry) => entry.level === level)?.skills ?? [];
  // Глубина: крупная ступень «ближе» и идёт за мышью, карточка урока — навстречу.
  const near = usePointerParallax(10);
  const far = usePointerParallax(-6);
  return (
    <section className="glass grid gap-6 overflow-hidden rounded-card border border-line bg-surface/70 p-5 text-heading shadow-card-lg sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:p-8">
      <div className="flex min-w-0 items-center gap-5">
        <motion.span
          style={{
            x: near.x,
            y: near.y,
            textShadow: '0 0 22px rgb(var(--c-teal)/0.55), 0 0 44px rgb(var(--c-accent)/0.35)',
          }}
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 14 }}
          className="flex flex-col items-center font-heading leading-none"
          aria-label={t('dashboard.levelOf', { step: levelStep(level), total: LEVEL_COUNT })}
        >
          <span className="text-6xl font-black sm:text-7xl">{levelStep(level)}</span>
          <span className="mt-1 whitespace-nowrap text-xs font-bold uppercase tracking-wide text-muted">{t('dashboard.ofTotal', { total: LEVEL_COUNT })}</span>
        </motion.span>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="t-eyebrow text-teal-deep">{t('profile.sections.level')}</span>
          <span className="t-h2">{levelName(level)}</span>
          <span className="text-sm leading-snug text-muted">{levelCanDo(level)}</span>
        </div>
      </div>

      <motion.div style={{ x: far.x, y: far.y }} className="flex min-w-0 flex-col gap-3 rounded-2xl bg-page/70 p-5 ring-1 ring-line">
        {!overview.map.contentReady ? (
          <CourseComingSoon language={overview.map.language} compact />
        ) : overview.map.next !== null ? (
          // Шаг плана с сервера: урок, закрепление, проверка знаний или финиш — без «урок N из 120».
          <NextStepCard step={overview.map.next} skills={skills} compact />
        ) : (
          <Button onClick={() => navigate('/map')}>{t('dashboard.openBrain')}</Button>
        )}
      </motion.div>
    </section>
  );
}

/** После скольких уроков позвать выбрать тариф: ученик уже понял, за что платить. */
const PLANS_AFTER_LESSONS = 3;

export function DashboardScreen() {
  const profile = useProfile();
  const { state, reload } = useLearningOverview();
  const email = useUserStore((state) => state.email);

  const { t } = useTranslation();
  const ready = state.kind === 'ready' ? state.overview : null;
  // Новичку на A1 без уроков — первым делом основы в справочнике.
  const sandbox = useSandbox(ready?.map.currentLevel ?? null, ready?.completed ?? null);
  // Следующий урок прогреваем в фоне, только если уроки уже открыты:
  // задержку «оседлости» и видимость вкладки держит сам хук.
  const nextLessonId = ready?.map.next?.kind === 'LESSON' ? ready.map.next.skillId : null;
  useLessonPrefetch(sandbox.active ? null : nextLessonId);

  if (state.kind === 'loading') {
    return (
      <div className="shell grid min-h-[50vh] place-items-center">
        <Quokka mood="thinking" alt={t('dashboard.loadingAlt')} size="lg" />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className="shell flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
        <Quokka mood="error" alt={t('dashboard.errorAlt')} size="lg" />
        <p className="text-heading">{t('dashboard.loadFailed')}</p>
        <Button onClick={reload}>{t('common.retry')}</Button>
      </div>
    );
  }

  const { overview } = state;
  return (
    <section className="shell flex flex-col gap-6 lg:gap-8">
      {/* Порядок: цифры роста, следующий урок. Сервисы — на своей вкладке. */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <ScrollReveal index={0} className="min-w-0">
          <Stat icon={IconBrain} value={`${Math.round((overview.map.mastery?.progress ?? 0) * 100)}%`} label={t('mastery.stat')} tone="bg-teal/20 text-teal-deep" />
        </ScrollReveal>
        <ScrollReveal index={1} className="min-w-0">
          <Stat icon={IconCards} value={`${overview.pocketCount}`} label={t('dashboard.pocketPhrases', { count: overview.pocketCount })} tone="bg-accent/25 text-accent-dark" />
        </ScrollReveal>
        <ScrollReveal index={2} className="min-w-0">
          <Stat icon={IconSparkles} value={`${overview.mastered}`} label={t('dashboard.skillsMastered')} tone="bg-correct/20 text-correct" />
        </ScrollReveal>
        <ScrollReveal index={3} className="min-w-0">
          <Stat icon={IconFlame} value={`${profile.streak}`} label={t('dashboard.streakDays', { count: profile.streak })} tone="bg-danger/15 text-danger" />
        </ScrollReveal>
      </div>

      {/* Песочница вместо «Следующего урока»: уроки ещё закрыты. */}
      {sandbox.active ? <SandboxCard sandbox={sandbox} variant="invite" /> : <Hero overview={overview} />}

      {/* Повод вернуться сегодня: фразы, которые вот-вот забудутся. */}
      <ReviewDueCard />

      {/* Одна карточка-предложение за раз, по важности: сохранить прогресс,
          затем тарифы после беты (после нескольких уроков). */}
      {overview.completed >= 1 && email === null ? (
        <SaveProgressCard />
      ) : overview.completed >= PLANS_AFTER_LESSONS && overview.profile.planIntent === null ? (
        <PlansCard />
      ) : null}
    </section>
  );
}
